import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import type { StatusTone } from "@/lib/types";

const toneClasses: Record<StatusTone, string> = {
  ready: "bg-rs-success-soft text-rs-success",
  scheduled: "bg-rs-info-soft text-rs-info",
  syncing: "bg-rs-accent-soft text-rs-accent-ink",
  synced: "bg-rs-success-soft text-rs-success",
  needs_configuration: "bg-rs-warning-soft text-rs-warning",
  warning: "bg-rs-warning-soft text-rs-warning",
  failed: "bg-rs-danger-soft text-rs-danger",
  completed: "bg-rs-muted text-rs-ink-muted",
  paused: "bg-rs-muted text-rs-ink-muted",
  auth_required: "bg-rs-warning-soft text-rs-warning",
  neutral: "bg-rs-muted text-rs-ink-muted",
};

export function StatusBadge({
  tone,
  children,
  className,
}: {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
        toneClasses[tone],
        className,
      )}
    >
      <span className="sr-only">Status:</span>
      {children}
    </span>
  );
}

export function conferenceStatusTone(status: string): StatusTone {
  switch (status) {
    case "active":
    case "trial":
      return "syncing";
    case "draft":
      return "needs_configuration";
    case "past_due":
      return "warning";
    case "cancelled":
    case "inactive":
      return "paused";
    default:
      return "neutral";
  }
}

export function destinationLifecycleTone(lifecycle: string): StatusTone {
  switch (lifecycle) {
    case "active":
      return "syncing";
    case "tested":
      return "ready";
    case "draft":
      return "needs_configuration";
    case "paused":
      return "paused";
    case "archived":
    case "disabled":
      return "failed";
    default:
      return "neutral";
  }
}

export function jobStatusTone(status: string): StatusTone {
  switch (status) {
    case "succeeded":
      return "synced";
    case "succeeded_with_warnings":
      return "warning";
    case "failed":
      return "failed";
    case "running":
    case "delivering":
      return "syncing";
    case "queued":
      return "scheduled";
    case "cancelled":
      return "paused";
    default:
      return "neutral";
  }
}

export function formatJobStatus(status: string): string {
  switch (status) {
    case "succeeded":
      return "Succeeded";
    case "succeeded_with_warnings":
      return "Succeeded with Warnings";
    case "failed":
      return "Failed";
    case "running":
      return "Running";
    case "delivering":
      return "Delivering";
    case "queued":
      return "Queued";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}
